import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Target, Users, Lightbulb, Award, Globe2 } from "lucide-react";
import FAQSection from "../components/FAQSection";
import { aboutFaqs } from "../data/faqs";
import BlogSection from "../components/BlogSection"; 

export const About = () => {
  const values = [
    { icon: Target, title: "Mission-Driven", description: "Empowering businesses with cutting-edge technology solutions" },
    { icon: Lightbulb, title: "Innovation First", description: "Always exploring new technologies and methodologies" },
    { icon: Users, title: "Client-Centric", description: "Your success is our success, partnership over transactions" },
    { icon: Award, title: "Quality Obsessed", description: "No compromises on code quality, security, or performance" }
  ];

  const team = [
    { name: "Product Managers", count: "12+", description: "Strategic thinkers" },
    { name: "Developers", count: "80+", description: "Full-stack experts" },
    { name: "UI/UX Designers", count: "15+", description: "Creative visionaries" },
    { name: "QA Engineers", count: "20+", description: "Quality guardians" }
  ];

  return (
    <>
      <Helmet>
        <title>About Us | Novaseeds Technologies</title>

        <meta
          name="description"
          content="Learn about Novaseeds Technologies, our mission, values, and world-class team delivering innovative software solutions globally."
        />

        {/* FAQ Schema Fix */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: aboutFaqs.map((faq) => ({
                "@type": "Question",
                name: faq.question,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: faq.answer
                }
              }))
            })
          }}
        />
      </Helmet>

      <div className="min-h-screen pt-32 pb-20 bg-background">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">

          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-20"
          >
            <span className="text-sm uppercase tracking-widest text-muted-foreground font-bold">
              ABOUT US
            </span>

            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none mt-4 mb-6">
              <span className="text-foreground">Building The Future</span><br />
              <span className="text-gradient">One Line of Code at a Time</span>
            </h1>

            <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-3xl mx-auto">
              Founded in 2012, Novaseeds Technologies has grown from a small startup
              to a global software development company delivering innovative
              technology solutions to businesses worldwide.
            </p>
          </motion.div>

          {/* Story Section */}
          <section className="mb-32">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <img
                  src="https://images.unsplash.com/photo-1630673470267-417e4d361129?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Our team"
                  className="rounded-3xl w-full h-auto shadow-2xl"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
                  Our Story
                </h2>

                <div className="space-y-4 text-base md:text-lg leading-relaxed text-muted-foreground">
                  <p>
                    What started as a vision to democratize technology for
                    businesses of all sizes has evolved into a mission-driven
                    organization helping companies worldwide transform their
                    operations through software.
                  </p>

                  <p>
                    Today our team works with companies across multiple
                    industries delivering scalable software solutions,
                    enterprise platforms, and digital products.
                  </p>

                  <p>
                    Our commitment remains unchanged: build exceptional software
                    that creates real value for our clients and their customers.
                  </p>
                </div>
              </motion.div>

            </div>
          </section>

          {/* Values */}
          <section className="mb-32">
            <motion.div className="text-center mb-16">
              <span className="text-sm uppercase tracking-widest text-muted-foreground font-bold">
                OUR VALUES
              </span>

              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mt-4 mb-6">
                What Drives Us
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => {
                const Icon = value.icon;

                return (
                  <motion.div
                    key={index}
                    className="bg-card border border-[#E2E8F0] shadow-[0_2px_8px_rgba(0,0,0,0.05)] p-8 rounded-3xl text-center"
                  >
                    <div className="w-16 h-16 rounded-2xl bg-blue-50 border border-blue-200 flex items-center justify-center mx-auto mb-6">
                      <Icon size={32} className="text-blue-600" />
                    </div>

                    <h3 className="text-2xl font-medium text-foreground mb-3">
                      {value.title}
                    </h3>

                    <p className="text-muted-foreground">
                      {value.description}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </section>

          {/* Team */}
          <section className="mb-32 bg-muted/50 border border-[#E2E8F0] rounded-3xl p-12">

            <motion.div className="text-center mb-16">
              <span className="text-sm uppercase tracking-widest text-muted-foreground font-bold">
                OUR TEAM
              </span>

              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mt-4 mb-6">
                World-Class Talent
              </h2>

              <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-3xl mx-auto">
                Our diverse team of experts brings together decades of
                experience building world-class software products.
              </p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {team.map((role, index) => (
                <div key={index} className="text-center">
                  <div className="text-5xl font-bold text-gradient mb-2">
                    {role.count}
                  </div>

                  <div className="text-foreground font-semibold mb-1">
                    {role.name}
                  </div>

                  <div className="text-muted-foreground text-sm">
                    {role.description}
                  </div>
                </div>
              ))}
            </div>

          </section>

          {/* Global Reach */}
          <section className="mb-32">
            <div className="bg-blue-50 border border-blue-200 rounded-3xl p-12 text-center">

              <Globe2 size={64} className="text-blue-600 mx-auto mb-6" />

              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
                Global Reach, Local Impact
              </h2>

              <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-3xl mx-auto mb-8">
                Serving clients across North America, Europe, Asia and
                Australia with seamless collaboration.
              </p>

              <div className="flex flex-wrap justify-center gap-4">
                {["USA","UK","Canada","Australia","Germany","Singapore","UAE","India"].map((country,index)=>(
                  <span
                    key={index}
                    className="bg-card border border-[#E2E8F0] rounded-full px-6 py-2 text-muted-foreground text-sm font-medium shadow-[0_2px_8px_rgba(0,0,0,0.05)]"
                  >
                    {country}
                  </span>
                ))}
              </div>

            </div>
          </section>
          {/* Blog Section */}
          <BlogSection categorySlug="ai-software-review" title="AI Trends & Thoughts" maxPosts={3} />
          {/* FAQ Section */}
          <FAQSection faqs={aboutFaqs} />
        

        </div>
      </div>
    </>
  );
};

